import { from, Observable, of } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import Keycloak, { KeycloakLoginOptions } from 'keycloak-js';
import { LocalStorageService } from './local-storage.service';
import { SessionStorageService } from './session-storage.service';
import { SubjectHolder } from './subject-holder';

export type SolLoginOptions = Pick<KeycloakLoginOptions, 'prompt' | 'redirectUri' | 'idpHint'>;

export interface AuthenticationState {
  enabled: boolean;
  fetched: boolean;
  name?: string;
  token?: string;
  email?: string;
  sessionId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService extends SubjectHolder<AuthenticationState> {
  // Dependencies
  private readonly keycloak = inject(Keycloak, { optional: true });
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly localStorageService = inject(LocalStorageService);

  constructor() {
    super({
      enabled: false,
      fetched: false,
    });
  }

  public fetch(): void {
    // Already fetched
    if (this.state.fetched) {
      return;
    }

    // No keycloak
    const keycloak = this.keycloak;
    if (!keycloak) {
      this.patch({
        token: undefined,
        name: '',
        email: undefined,
        sessionId: undefined,
      });
      return;
    }

    // Parse token
    this.updateFromKeycloak(keycloak);
  }

  public initialize(): void {
    const keycloak = this.keycloak;
    if (!keycloak) {
      return;
    }
    this.patch({ enabled: true });
    this.registerKeycloakEvents(keycloak);
  }

  public login(options: SolLoginOptions): Observable<void> {
    const keycloak = this.keycloak;
    if (!keycloak) {
      return of();
    }
    return from(keycloak.login(options));
  }

  public logout(): Observable<void> {
    const keycloak = this.keycloak;
    if (!keycloak) {
      return of();
    }
    this.resetState();
    return from(keycloak.logout({ redirectUri: document.baseURI }));
  }

  public goToProfile(): Observable<void> {
    if (!this.keycloak) {
      return of();
    }
    return from(this.keycloak.accountManagement());
  }

  private registerKeycloakEvents(keycloak: Keycloak): void {
    keycloak.onAuthSuccess = () => {
      this.updateFromKeycloak(keycloak);
    };
    keycloak.onAuthRefreshSuccess = () => {
      this.updateFromKeycloak(keycloak);
    };
    keycloak.onAuthLogout = () => {
      this.resetState();
    };
  }

  private updateFromKeycloak(keycloak: Keycloak): void {
    this.patch({
      fetched: true,
      token: keycloak.token,
      name: !keycloak.tokenParsed ? undefined : (keycloak.tokenParsed['name'] as string) || undefined,
      email: !keycloak.tokenParsed ? undefined : (keycloak.tokenParsed['email'] as string) || undefined,
      sessionId: !keycloak.tokenParsed ? undefined : (keycloak.tokenParsed['sid'] as string) || undefined,
    });
  }

  private resetState(): void {
    this.patch({
      fetched: false,
      token: undefined,
      name: '',
      email: undefined,
      sessionId: undefined,
    });
    this.clearStorage();
  }

  private clearStorage() {
    this.sessionStorageService.clear();
    this.localStorageService.clear();
  }
}
